/**
 * Field-aware keyboard avoidance (iOS + Android).
 *
 * Strict rules:
 * 1. Measure the **focused** field vs keyboard top.
 * 2. If it does **not** overlap → keep current lift within hysteresis (no oscillation).
 * 3. If it overlaps → scroll when there is room; only then lift by leftover overlap.
 * 4. Never pad/lift for "keyboard is open" alone — only for real coverage of the focused field.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, type KeyboardEvent, type LayoutChangeEvent, type View } from 'react-native';

const DEFAULT_CLEARANCE_PX = 12;
const MAX_SETTLE_PASSES = 8;
const SETTLE_MS = 48;
/** Extra wait after pad/lift so layout + host transform settle before remeasure. */
const AFTER_MOVE_MS = 64;
/** Ignore content-size bumps closer than this (pad → size change → bump loop). */
const CONTENT_BUMP_DEBOUNCE_MS = 80;

type Scrollable = {
  scrollTo: (opts: { y: number; animated?: boolean }) => void;
};

type UseFormFieldFocusScrollOptions = {
  enabled?: boolean;
  scroll_ref: React.RefObject<Scrollable | null>;
  scroll_y_ref: React.MutableRefObject<number>;
  /** Absolute upward panel lift in px (0 = none). Host applies as translateY. */
  on_panel_offset?: (offset_px: number) => void;
  clearance_px?: number;
};

function measure_in_window(node: View): Promise<{ y: number; h: number } | null> {
  return new Promise(resolve => {
    try {
      node.measureInWindow((_x, y, _w, h) => {
        if (typeof y !== 'number' || typeof h !== 'number' || Number.isNaN(y) || Number.isNaN(h) || h <= 0) {
          resolve(null);
          return;
        }
        resolve({ y, h });
      });
    } catch {
      resolve(null);
    }
  });
}

function wait_ms(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function read_keyboard_top(e?: KeyboardEvent): number {
  const from_event = e?.endCoordinates?.screenY;
  if (typeof from_event === 'number' && from_event > 0) {
    return from_event;
  }
  const metrics = Keyboard.metrics();
  if (metrics != null && typeof metrics.screenY === 'number' && metrics.screenY > 0) {
    return metrics.screenY;
  }
  return 0;
}

export function useFormFieldFocusScroll({
  enabled = true,
  scroll_ref,
  scroll_y_ref,
  on_panel_offset,
  clearance_px = DEFAULT_CLEARANCE_PX,
}: UseFormFieldFocusScrollOptions) {
  const keyboard_top_ref = useRef(0);
  const focused_wrap_ref = useRef<View | null>(null);
  const panel_lift_ref = useRef(0);
  const generation_ref = useRef(0);
  const run_token_ref = useRef(0);
  const scroll_layout_h_ref = useRef(0);
  const scroll_content_h_ref = useRef(0);
  const scroll_pad_ref = useRef(0);
  const bump_ref = useRef<() => void>(() => undefined);
  const content_bump_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Extra bottom padding only when a covered field needs scroll room it doesn't have yet.
   * Never set to full keyboard height just because the keyboard opened.
   */
  const [keyboard_scroll_pad, setKeyboardScrollPad] = useState(0);

  const set_scroll_pad = useCallback((next: number) => {
    const clamped = Math.max(0, Math.ceil(next));
    if (clamped === scroll_pad_ref.current) {
      return;
    }
    scroll_pad_ref.current = clamped;
    setKeyboardScrollPad(clamped);
  }, []);

  const set_panel_lift = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.ceil(next));
      if (clamped === panel_lift_ref.current) {
        return;
      }
      panel_lift_ref.current = clamped;
      on_panel_offset?.(clamped);
    },
    [on_panel_offset],
  );

  const clear_avoidance = useCallback(() => {
    set_panel_lift(0);
    set_scroll_pad(0);
  }, [set_panel_lift, set_scroll_pad]);

  const bump_focus_above_keyboard = useCallback(async () => {
    const token = ++run_token_ref.current;
    const generation = generation_ref.current;

    if (!enabled) {
      clear_avoidance();
      return;
    }

    let scrolled_this_run = false;
    let padded_this_run = false;

    for (let pass = 0; pass < MAX_SETTLE_PASSES; pass++) {
      if (token !== run_token_ref.current || generation !== generation_ref.current) {
        return;
      }

      const node = focused_wrap_ref.current;
      let keyboard_top = keyboard_top_ref.current;
      if (keyboard_top <= 0) {
        keyboard_top = read_keyboard_top();
        keyboard_top_ref.current = keyboard_top;
      }

      if (!node || keyboard_top <= 0) {
        if (keyboard_top <= 0 && !Keyboard.isVisible()) {
          clear_avoidance();
        }
        return;
      }

      const measured = await measure_in_window(node);
      if (token !== run_token_ref.current || generation !== generation_ref.current) {
        return;
      }
      if (measured == null) {
        await wait_ms(SETTLE_MS);
        continue;
      }

      const field_bottom = measured.y + measured.h;
      const target_bottom = keyboard_top - clearance_px;
      const overlap = field_bottom - target_bottom;

      // Field clear of keyboard — hysteresis so a successful lift does not immediately unwind.
      if (overlap <= 0) {
        const prev_lift = panel_lift_ref.current;
        const prev_pad = scroll_pad_ref.current;
        const spare = -overlap;

        // Fully clear with lots of spare: reset so top fields never leave a floating card.
        if (spare > clearance_px * 2 && (prev_lift > 0 || prev_pad > 0)) {
          clear_avoidance();
          await wait_ms(SETTLE_MS);
          continue;
        }

        // Within clearance (or already at rest): stay put — prevents login lift oscillation.
        return;
      }

      const scroll_room = Math.max(0, scroll_content_h_ref.current - scroll_layout_h_ref.current - 1);

      // Need overlap cleared but not enough scroll room → add only the shortfall as pad (once).
      if (!padded_this_run && scroll_ref.current != null && scroll_room < overlap) {
        const shortfall = overlap - scroll_room + clearance_px;
        set_scroll_pad(Math.max(scroll_pad_ref.current, shortfall));
        padded_this_run = true;
        await wait_ms(AFTER_MOVE_MS);
        continue;
      }

      if (!scrolled_this_run && scroll_ref.current != null && scroll_layout_h_ref.current > 0 && scroll_room > 0) {
        const next_y = Math.min(scroll_y_ref.current + overlap, scroll_y_ref.current + scroll_room);
        const clamped_y = Math.max(0, next_y);
        if (clamped_y > scroll_y_ref.current + 0.5) {
          scroll_ref.current.scrollTo({ y: clamped_y, animated: false });
          scroll_y_ref.current = clamped_y;
          scrolled_this_run = true;
          await wait_ms(AFTER_MOVE_MS);
          continue;
        }
        scrolled_this_run = true;
      }

      // Absolute lift: measure already includes current transform — add remaining overlap once.
      set_panel_lift(panel_lift_ref.current + overlap);
      await wait_ms(AFTER_MOVE_MS);
    }
  }, [clear_avoidance, clearance_px, enabled, scroll_ref, scroll_y_ref, set_panel_lift, set_scroll_pad]);

  bump_ref.current = () => {
    void bump_focus_above_keyboard();
  };

  useEffect(() => {
    if (!enabled) {
      clear_avoidance();
      return;
    }

    const on_show = (e: KeyboardEvent) => {
      keyboard_top_ref.current = read_keyboard_top(e);
      const duration = typeof e.duration === 'number' && e.duration > 0 ? e.duration : Platform.OS === 'ios' ? 250 : 0;
      requestAnimationFrame(() => {
        void bump_focus_above_keyboard();
      });
      setTimeout(() => {
        keyboard_top_ref.current = read_keyboard_top();
        void bump_focus_above_keyboard();
      }, Math.max(duration, 60) + 24);
    };

    const on_hide = () => {
      keyboard_top_ref.current = 0;
      generation_ref.current += 1;
      run_token_ref.current += 1;
      clear_avoidance();
    };

    // One show + one hide per platform — dual will/did listeners double-fire and fight lift.
    if (Platform.OS === 'ios') {
      const sub_will_show = Keyboard.addListener('keyboardWillShow', on_show);
      const sub_will_hide = Keyboard.addListener('keyboardWillHide', on_hide);
      return () => {
        sub_will_show.remove();
        sub_will_hide.remove();
      };
    }

    const sub_did_show = Keyboard.addListener('keyboardDidShow', on_show);
    const sub_did_hide = Keyboard.addListener('keyboardDidHide', on_hide);
    return () => {
      sub_did_show.remove();
      sub_did_hide.remove();
    };
  }, [bump_focus_above_keyboard, clear_avoidance, enabled]);

  const on_field_focus = useCallback(
    (wrap: React.RefObject<View | null>) => {
      if (!enabled) {
        return;
      }
      generation_ref.current += 1;
      focused_wrap_ref.current = wrap.current;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (keyboard_top_ref.current <= 0) {
            keyboard_top_ref.current = read_keyboard_top();
          }
          // Keyboard not up yet — remember focus target; wait for will/didShow. Do not clear.
          if (keyboard_top_ref.current <= 0 && !Keyboard.isVisible()) {
            return;
          }
          void bump_focus_above_keyboard();
        });
      });
    },
    [bump_focus_above_keyboard, enabled],
  );

  const on_scroll_layout = useCallback((e: LayoutChangeEvent) => {
    scroll_layout_h_ref.current = e.nativeEvent.layout.height;
  }, []);

  const on_scroll_content_size_change = useCallback((_w: number, h: number) => {
    scroll_content_h_ref.current = h;
    if (keyboard_top_ref.current <= 0 || focused_wrap_ref.current == null) {
      return;
    }
    if (content_bump_timer_ref.current != null) {
      clearTimeout(content_bump_timer_ref.current);
    }
    content_bump_timer_ref.current = setTimeout(() => {
      content_bump_timer_ref.current = null;
      bump_ref.current();
    }, CONTENT_BUMP_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (content_bump_timer_ref.current != null) {
        clearTimeout(content_bump_timer_ref.current);
      }
    };
  }, []);

  return {
    on_field_focus,
    clear_panel_offset: clear_avoidance,
    on_scroll_layout,
    on_scroll_content_size_change,
    /** Only non-zero when a covered field needs extra scroll room. */
    keyboard_scroll_pad,
  };
}
